"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.jobFormOptionsResponseSchema = exports.jobListResponseSchema = exports.jobDetailSchema = exports.jobSummarySchema = exports.jobLineSchema = exports.appointmentSchema = exports.jobOperationalLocationSchema = exports.jobServiceLocationSchema = exports.jobCustomerSchema = exports.reorderJobLinesRequestSchema = exports.updateJobLineRequestSchema = exports.addJobLineRequestSchema = exports.updateAppointmentStatusRequestSchema = exports.addAppointmentRequestSchema = exports.updateJobRequestSchema = exports.createJobRequestSchema = exports.appointmentInputSchema = exports.appointmentStatusSchema = exports.jobStatusSchema = exports.jobPrioritySchema = exports.appointmentIdSchema = exports.jobLineIdSchema = exports.jobIdSchema = void 0;
const zod_1 = require("zod");
const catalog_1 = require("./catalog");
const job_checklist_1 = require("./job-checklist");
const pagination_1 = require("./pagination");
const team_1 = require("./team");
const optionalTrimmedString = (maxLength) => zod_1.z
    .string()
    .trim()
    .max(maxLength)
    .optional()
    .transform((value) => value || undefined);
const nullableTrimmedString = (maxLength) => zod_1.z
    .union([zod_1.z.string().trim().max(maxLength), zod_1.z.null()])
    .optional()
    .transform((value) => (value === undefined ? undefined : value || null));
exports.jobIdSchema = zod_1.z.string().uuid();
exports.jobLineIdSchema = zod_1.z.string().uuid();
exports.appointmentIdSchema = zod_1.z.string().uuid();
exports.jobPrioritySchema = zod_1.z.enum(["LOW", "NORMAL", "HIGH", "URGENT"]);
exports.jobStatusSchema = zod_1.z.enum([
    "NEW",
    "SCHEDULED",
    "IN_PROGRESS",
    "COMPLETED",
    "CANCELED",
]);
exports.appointmentStatusSchema = zod_1.z.enum([
    "SCHEDULED",
    "EN_ROUTE",
    "IN_PROGRESS",
    "COMPLETED",
    "CANCELED",
]);
exports.appointmentInputSchema = zod_1.z
    .object({
    startAt: zod_1.z.string().datetime(),
    endAt: zod_1.z.string().datetime(),
    assignedMembershipId: zod_1.z.string().uuid().optional(),
})
    .refine((value) => Date.parse(value.endAt) > Date.parse(value.startAt), {
    path: ["endAt"],
    message: "End time must be after start time.",
});
exports.createJobRequestSchema = zod_1.z.object({
    customerId: zod_1.z.string().uuid(),
    serviceLocationId: zod_1.z.string().uuid(),
    businessLocationId: zod_1.z.string().uuid().optional(),
    summary: zod_1.z.string().trim().min(2).max(160),
    description: optionalTrimmedString(5_000),
    priority: exports.jobPrioritySchema.default("NORMAL"),
    appointment: exports.appointmentInputSchema.optional(),
});
exports.updateJobRequestSchema = zod_1.z
    .object({
    summary: zod_1.z.string().trim().min(2).max(160).optional(),
    description: nullableTrimmedString(5_000),
    priority: exports.jobPrioritySchema.optional(),
})
    .refine((value) => value.summary !== undefined ||
    value.description !== undefined ||
    value.priority !== undefined, { message: "At least one job field is required." });
exports.addAppointmentRequestSchema = exports.appointmentInputSchema;
exports.updateAppointmentStatusRequestSchema = zod_1.z.object({
    status: exports.appointmentStatusSchema,
});
const jobLineQuantityMilliSchema = zod_1.z.number().int().min(1).max(1_000_000);
const jobLineMoneySchema = zod_1.z.number().int().min(0).max(100_000_000);
const jobLineDescriptionSchema = zod_1.z
    .union([zod_1.z.string().trim().max(2_000), zod_1.z.null()])
    .transform((value) => value || null);
const jobLineNotesSchema = zod_1.z
    .union([zod_1.z.string().trim().max(2_000), zod_1.z.null()])
    .transform((value) => value || null);
const jobLineUnitDurationMinutesSchema = zod_1.z
    .number()
    .int()
    .min(5)
    .max(1_440)
    .nullable();
exports.addJobLineRequestSchema = zod_1.z.discriminatedUnion("source", [
    zod_1.z.object({
        source: zod_1.z.literal("CATALOG"),
        catalogItemId: zod_1.z.string().uuid(),
        quantityMilli: jobLineQuantityMilliSchema.default(1_000),
        notes: jobLineNotesSchema.optional().default(null),
    }),
    zod_1.z.object({
        source: zod_1.z.literal("CUSTOM"),
        name: zod_1.z.string().trim().min(2).max(160),
        description: jobLineDescriptionSchema.optional().default(null),
        type: zod_1.z.enum(["SERVICE", "MATERIAL"]),
        pricingUnit: zod_1.z.enum(["FLAT_RATE", "HOUR", "EACH"]),
        quantityMilli: jobLineQuantityMilliSchema,
        unitDurationMinutes: jobLineUnitDurationMinutesSchema
            .optional()
            .default(null),
        notes: jobLineNotesSchema.optional().default(null),
        unitPriceCents: jobLineMoneySchema,
        unitCostCents: jobLineMoneySchema.nullable().optional().default(null),
        taxable: zod_1.z.boolean(),
    }),
]);
exports.updateJobLineRequestSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(2).max(160).optional(),
    description: jobLineDescriptionSchema.optional(),
    quantityMilli: jobLineQuantityMilliSchema.optional(),
    unitDurationMinutes: jobLineUnitDurationMinutesSchema.optional(),
    notes: jobLineNotesSchema.optional(),
    unitPriceCents: jobLineMoneySchema.optional(),
    taxable: zod_1.z.boolean().optional(),
})
    .refine((value) => Object.values(value).some((item) => item !== undefined), {
    message: "At least one line item field is required.",
});
exports.reorderJobLinesRequestSchema = zod_1.z.object({
    lineIds: zod_1.z
        .array(exports.jobLineIdSchema)
        .min(1)
        .max(100)
        .refine((lineIds) => new Set(lineIds).size === lineIds.length, {
        message: "Line item IDs must be unique.",
    }),
});
exports.jobCustomerSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    displayName: zod_1.z.string(),
});
exports.jobServiceLocationSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    name: zod_1.z.string(),
    addressLine1: zod_1.z.string(),
    addressLine2: zod_1.z.string().nullable(),
    city: zod_1.z.string(),
    state: zod_1.z.string(),
    postalCode: zod_1.z.string(),
    timezone: zod_1.z.string(),
});
exports.jobOperationalLocationSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    name: zod_1.z.string(),
    timezone: zod_1.z.string(),
    isDefault: zod_1.z.boolean(),
});
exports.appointmentSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    startAt: zod_1.z.string().datetime(),
    endAt: zod_1.z.string().datetime(),
    status: exports.appointmentStatusSchema,
    assignee: team_1.teamMemberSchema.nullable(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.jobLineSchema = zod_1.z.object({
    id: exports.jobLineIdSchema,
    catalogItemId: zod_1.z.string().uuid().nullable(),
    position: zod_1.z.number().int().nonnegative(),
    name: zod_1.z.string(),
    description: zod_1.z.string().nullable(),
    type: zod_1.z.enum(["SERVICE", "MATERIAL"]),
    pricingUnit: zod_1.z.enum(["FLAT_RATE", "HOUR", "EACH"]),
    quantityMilli: zod_1.z.number().int().positive(),
    unitDurationMinutes: jobLineUnitDurationMinutesSchema,
    notes: zod_1.z.string().max(2_000).nullable(),
    unitPriceCents: zod_1.z.number().int().nonnegative(),
    unitCostCents: zod_1.z.number().int().nonnegative().nullable(),
    taxable: zod_1.z.boolean(),
    lineTotalCents: zod_1.z.number().int().nonnegative(),
    priceBookVersionId: zod_1.z.string().uuid().nullable().optional(),
});
exports.jobSummarySchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    jobNumber: zod_1.z.number().int().positive(),
    sourceEstimateId: zod_1.z.string().uuid().nullable(),
    summary: zod_1.z.string(),
    description: zod_1.z.string().nullable(),
    priority: exports.jobPrioritySchema,
    status: exports.jobStatusSchema,
    customer: exports.jobCustomerSchema,
    serviceLocation: exports.jobServiceLocationSchema,
    operationalLocation: exports.jobOperationalLocationSchema,
    subtotalCents: zod_1.z.number().int().nonnegative(),
    taxableSubtotalCents: zod_1.z.number().int().nonnegative(),
    taxRateMilliPercent: zod_1.z.number().int().nonnegative(),
    taxCents: zod_1.z.number().int().nonnegative(),
    totalCents: zod_1.z.number().int().nonnegative(),
    nextAppointment: exports.appointmentSchema.nullable(),
    appointmentCount: zod_1.z.number().int().nonnegative(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.jobDetailSchema = exports.jobSummarySchema
    .omit({ nextAppointment: true, appointmentCount: true })
    .extend({
    appointments: zod_1.z.array(exports.appointmentSchema),
    checklistCompletion: job_checklist_1.jobChecklistCompletionSchema,
    checklists: zod_1.z.array(job_checklist_1.jobChecklistSchema),
    lines: zod_1.z.array(exports.jobLineSchema),
});
exports.jobListResponseSchema = zod_1.z.object({
    items: zod_1.z.array(exports.jobSummarySchema),
    total: zod_1.z.number().int().nonnegative(),
    pageInfo: pagination_1.pageInfoSchema.default(pagination_1.defaultPageInfo),
});
exports.jobFormOptionsResponseSchema = zod_1.z.object({
    customers: zod_1.z.array(exports.jobCustomerSchema.extend({
        locations: zod_1.z.array(exports.jobServiceLocationSchema),
    })),
    operationalLocations: zod_1.z.array(exports.jobOperationalLocationSchema),
    catalogItems: zod_1.z.array(catalog_1.catalogItemSchema),
    teamMembers: zod_1.z.array(team_1.teamMemberSchema),
});
//# sourceMappingURL=job.js.map